import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  NgZone,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import * as Highcharts from 'highcharts';
import { CheckBoxSelector } from 'src/app/domain/shared/component/checkbox-selector/checkbox-selector.component';
import { calculateSum, CHART_COLORS, CHART_FONTS, CHART_STYLES }
  from 'src/app/domain/shared/component/pie-chart-simple/highcharts-common/highcharts-global-const';


@Component({
  selector: 'app-portfolio-performance-chart',
  templateUrl: './portfolio-performance-chart.component.html',
  styleUrls: ['./portfolio-performance-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortfolioPerformanceChartComponent implements OnChanges, AfterViewInit {

  @Input() fundPerformances = [];
  @Input() chartPeriods: CheckBoxSelector[];
  @Input() updateTrigger: boolean;
  @Input() showTitle = true;
  @Input() selector: 'total' | 'active' | 'sold' = 'total';
  @Input() forInvReport = false;
  @Input() showEmptyState = false;
  @Input() parent: 'fp-fund' | 'fp-value-creation' | 'investor-report' | 'published-investor-report' = 'fp-fund';

  alreadyRendered = false;
  min: number;
  max: number;
  options;

  constructor(private zone: NgZone) {}

  returnOptions(): void {
    this.options = {
      chart: {
        style: {
          fontFamily: 'Inter',
        },
        events: CHART_STYLES.eventForScrollingWithManualRedraw,
      },
      customRange: 19,
      title: undefined,
      xAxis: [{
        labels: {
          style: { fontSize: CHART_FONTS.font4, color: '#78909C' },
          format: '{value.period}<span style="font-weight: 500; color: #00948E">{value.isPredicted}<span>',
        },
        categories: [],
        type: 'category',
        crosshair: true,
        scrollbar: CHART_STYLES.scrollbar,
      }],
      yAxis: [{ // Primary yAxis
        title: {
          text: undefined,
        },
        labels: {
          format: '{value}M',
          style: { fontSize: CHART_FONTS.font4, color: CHART_COLORS.black12 },
        },
        stackLabels: {
          enabled: true,
          style: { color: CHART_COLORS.black12, fontSize: CHART_FONTS.font4 },
          formatter(): string {
            const sum = calculateSum(this.axis.series, this.x);
            if (sum !== 0 && ((sum < 0 && this.isNegative) || (sum >= 0 && !this.isNegative))) {
              const decimals = sum > 100 ? 0 : 1;
              return Highcharts.numberFormat(sum, decimals);
            }
          },
        },
      },
      { // Secondary yAxis
        title: {
          text: 'MOIC',
          style: { color: CHART_COLORS.black12, fontSize: CHART_FONTS.font3 },
        },
        tickAmount: 9,
        min: 0,
        labels: {
          format: '{value}',
          style: { color: CHART_COLORS.black12, fontSize: CHART_FONTS.font4 },
        },
        opposite: true,
      }],
      responsive: CHART_STYLES.responsiveLegendTwoYAxis,
      exporting: { enabled: false },
      tooltip: {
        shared: true,
        borderColor: '#EEEFF0',
        backgroundColor: 'white',
        borderRadius: 12,
        shadow: false,
        useHTML: true,
        headerFormat: '<span style="font-size: 0.8rem"><b><span style="color: #00948E">{point.key.isPredicted}</span>\
        {point.key.period}</b></span><br>',
        style: { fontSize: CHART_FONTS.font4 },
        valueSuffix: ' M',
        pointFormat: '{series.name}:<b> $ {point.y:.2f}M</b><br>',
        pointFormatter(): string {
          const colorDot = '<span style="color: ' + this.color + '">&#9679;</span>';
          // MOIC: x1.5
          // Investment: 15M
          if (this.y) {
            return colorDot + ' ' + this.series.name + (this.series.name === 'MOIC' ? '<b>: x' : ': <b>')
            + this.y.toFixed(2) + (this.series.name === 'MOIC' ? '' : 'M') + '</b><div>';
          }
          return colorDot + ' ' + this.series.name + ': <b>-</b><br>';
        },
      },
      legend: {
        itemStyle: { fontSize: '0.75rem' },
        align: 'left',
        verticalAlign: 'top',
        floating: false,
      },
      plotOptions: {
        column: {
          width: 100,
          stacking: 'normal',
          dataLabels: {
            enabled: true,
            inside: true,
            color: 'white',
            rotation: 270,
            format: '<b>{point.y:.1f}M</b>',
            style: {
              fontSize: CHART_FONTS.font4,
              textShadow: false,
              textOutline: 'none',
            },
          },
        },
      },
      credits: {
        enabled: false,
      },
      series: [{
        name: 'Net gain/(loss)',
        type: 'column',
        yAxis: 0,
        data: [],
        color: '#5CC3BB',
        colors: [],
      }, {
        name: 'Investment',
        type: 'column',
        yAxis: 0,
        data: [],
        color: '#5E6F78',
        colors: [],
      },
      {
        name: 'MOIC',
        type: 'spline',
        yAxis: 1,
        data: [],
        color: '#5E6F78',
        marker: {
          enabled: false,
          lineWidth: 3,
          fillColor: 'white',
        },
      }],
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.showEmptyState) return;
    if (this.fundPerformances && this.fundPerformances.length) {
      const periods: string[] = [];
      const cost: number[] = [];
      const gainLoss: number[] = [];
      const predicted: string[] = [];
      const gMult: number[] = [];
      const selectedPeriods = this.chartPeriods.filter((c) => c.chosen === true).map((c) => c.item);

      this.fundPerformances.sort((f1, f2) => new Date(f1.period).getTime() - new Date(f2.period).getTime());
      this.fundPerformances.forEach((el) => {
        const partialCompaniesAmount = this.selector === 'active' ? el.activeCompaniesAmount : el.soldCompaniesAmount;
        const compAmount = this.selector === 'total' ? el.totalCompanyAmount : partialCompaniesAmount;

        if (selectedPeriods.includes(el.period) && compAmount) {
          const partialCost = this.selector === 'active' ? el.activeCost : el.soldCost;
          const elCost = this.selector === 'total' ? el.totalCost : partialCost;

          const partialGain = this.selector === 'active' ? el.unrealized + el.escrow : el.realizedGain;
          const elGainLoss = this.selector === 'total' ? (el.unrealized + el.realizedGain + el.escrow) : partialGain;
          const moic = elCost ? (elCost + elGainLoss) / elCost : 0;

          /** Here is saved functionality, how to "blur = change color" of bars for predicted periods
              it was a problem, that legend was grey if there are two different colors
              Add colorByPoint: true and delete color in series to apply this functionality */
          // bgColors.gainLoss.push(el.isBasedOnPrognosis ? '#ADE1DD' : '#5CC3BB');
          // bgColors.cost.push(el.isBasedOnPrognosis ? '#AEB7BC' : '#5E6F78');
          predicted.push(el.isBasedOnPrognosis ? 'Predicted<br>' : undefined);

          periods.push(el.period.toString() + '<br>' + compAmount?.toString() + ' comp. <br>');
          cost.push(Number((elCost / 1000000).toFixed(2)));
          gainLoss.push(Number((elGainLoss / 1000000).toFixed(2)));
          gMult.push(Number(moic.toFixed(2)));
        }
      });

      this.min = 0;
      this.max = 0;

      this.arrayMin(gainLoss);
      this.arrayMax(cost, gainLoss);

      if (this.min > 0) this.min = 0;
      const interval = Number(((this.max - this.min) / 7).toFixed(0)) + 1;
      const ticks = [];

      let floor = Number((this.min / interval * -1).toFixed(0));
      if (this.min !== 0 && floor === 0) {
        floor++;
      }
      const positives = 8 - floor + 1;
      while (floor > 0) {
        ticks.push(interval * floor * -1);
        floor--;
      }

      for (let i = 0; i < positives; i++) {
        ticks.push(interval * i);
      }

      this.returnOptions();

      const newData = [];
      periods.forEach((value, i) => {
        newData.push({
          period: value,
          isPredicted: predicted[i],
        });
      });

      this.options.xAxis[0].categories = newData;
      this.options.series[0].data = gainLoss;
      this.options.series[1].data = cost;
      this.options.series[2].data = gMult;
      this.options.yAxis[0].tickPositions = ticks;

      this.options.plotOptions = {
        column: {
          width: 100,
          stacking: 'normal',
          dataLabels: {
            enabled: true,
            inside: true,
            color: 'white',
            rotation: 270,
            style: {
              fontSize: CHART_FONTS.font4,
              textShadow: false,
              textOutline: 'none',
            },
            formatter(): string {
              if (this.point.shapeArgs.height >= 60) {
                const decimals = this.y > 100 ? 0 : 1;
                return Highcharts.numberFormat(this.y, decimals);
              }
              this.point.visible = false; // <--hide this dataLabel
              return ''; // <--have to return "something" so, return an empty string
            },
          },
        },
      };

      Highcharts.setOptions({
        colors: ['#8BD4EA' /* skyblue*/, '#00B8AA'/* teal*/, '#37464A' /* charcoal*/,
          '#F2C816', '#5F6C6E', '#F16360', '#F79568', '#FFF263', '#A76A9A', '#DEBFBF', '#DEBFBF'],
        lang: {
          thousandsSep: ',',
          decimalPoint: '.',
        },
      });

      if (changes.updateTrigger && this.alreadyRendered) {
        this.render();
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.showEmptyState) return;
    this.render();
    this.alreadyRendered = true;
  }

  private render(): void {
    this.zone.runOutsideAngular(() => {
      Highcharts.chart((this.selector + this.parent), this.options);
    });
  }

  private arrayMin(arr): void {
    let len = arr.length;
    while (len--) {
      if (arr[len] < this.min) {
        this.min = arr[len];
      }
    }
  };

  private arrayMax(arr1, arr2): void {
    let len = arr1.length;
    while (len--) {
      const value = arr1[len] + arr2[len];
      if (value > this.max) {
        this.max = value;
      }
    }
  };
}
